import { Card, CardTitle, CardValue, StatCard } from '../components/ui/StatCard.jsx';
import PageHeader from '../components/ui/PageHeader.jsx';
import RevenueChart from '../components/ui/RevenueChart.jsx';
import { formatEuro, abbrevEuro } from '../lib/format.js';
import { useApi } from '../hooks/useApi.js';
import { useState } from 'react';

export default function DashboardPage() {
  const [period, setPeriod] = useState('month'); // 'month' | 'year'
  const { data, loading, error } = useApi(`/stats?period=${period}`, { deps: [period] });
  const { data: meta, loading: metaLoading } = useApi('/meta');
  const { data: settings, loading: settingsLoading } = useApi('/settings');

  if (error) {
    return (
      <PageHeader title="Dashboard" subtitle="Errore caricamento dati" />
    );
  }

  const statsData = data;

  return (
    <>
      <PageHeader
        title="Dashboard"
        subtitle="Panoramica conto economico e stato preventivo"
        action={
          <button
            className="btn btn-primary"
            onClick={() => window.open('/preventivi', '_self')}
          >
            + Nuovo preventivo
          </button>
        }
      />
      <div className="page-grid mb-6">
        <StatCard label="Clienti anagrafati" value={loading ? '—' : data?.counts?.clients ?? 0} tone="brand" />
        <StatCard label="Servizi in listino"  value={loading ? '—' : data?.counts?.services ?? 0} tone="info" />
        <StatCard label="Preventivi attivi"  value={loading ? '—' : data?.counts?.quotes ?? 0} tone="warning" />
        <StatCard
          label="Accettati"
          value={loading ? '—' : `${data?.counts?.accepted ?? 0} / ${abbrevEuro(data?.values?.accepted_value_eur ?? 0)}`}
          tone="success"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        {/* Grafico incassi - span 2 colonne */}
        <div className="lg:col-span-2">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-surface-3 uppercase tracking-wide">
              Andamento incassi {period === 'year' ? '(annuale)' : '(mensile)'}
            </h3>
            <div className="flex gap-1">
              <button
                className={`btn btn-xs ${period === 'month' ? 'btn-primary' : 'btn-ghost'}`}
                onClick={() => setPeriod('month')}
              >
                Mensile
              </button>
              <button
                className={`btn btn-xs ${period === 'year' ? 'btn-primary' : 'btn-ghost'}`}
                onClick={() => setPeriod('year')}
              >
                Annuale
              </button>
            </div>
          </div>
          <RevenueChart data={statsData?.byPeriod ?? []} period={period} />
        </div>

        {/* Preventivi per stato - 1 colonna */}
        <div className="card-elevated p-4">
          <h3 className="text-sm font-medium text-surface-3 uppercase tracking-wide mb-3">
            Preventivi per stato
          </h3>
          <div className="space-y-2">
            {data?.byStatus?.map((s) => (
              <div key={s.status} className="flex items-center justify-between">
                <span className="text-sm text-surface-2 capitalize">{s.status}</span>
                <span className="font-mono text-brand-300">{s.cnt}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Ultimi preventivi */}
      <div className="card-elevated">
        <h3 className="text-sm font-medium text-surface-3 uppercase tracking-wide p-4 border-b border-line">
          Ultimi preventivi
        </h3>
        <div className="overflow-x-auto">
          <table className="table-root">
            <thead>
              <tr>
                <th className="table-header">N°</th>
                <th className="table-header">Cliente</th>
                <th className="table-header">Data</th>
                <th className="table-header">Stato</th>
                <th className="table-header text-right">Totale</th>
              </tr>
            </thead>
            <tbody className="text-surface-2">
              {data?.recent?.map((q) => (
                <tr key={q.id} className="hover:bg-line/30 transition-colors">
                  <td className="table-cell font-mono text-brand-300">{q.number}</td>
                  <td className="table-cell">{q.display_name}</td>
                  <td className="table-cell">{q.issue_date}</td>
                  <td className="table-cell">
                    <span className={`badge badge-${q.tone || 'slate'}`}>{q.status}</span>
                  </td>
                  <td className="table-cell text-right font-mono">{formatEuro(q.total_amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

